import { useEffect, useRef } from "react";

const codes = [
  { kanji: "指針", roman: "DIRECTION", index: "01" },
  { kanji: "浄化", roman: "PURIFY", index: "02" },
  { kanji: "創造", roman: "CREATE", index: "03" },
  { kanji: "意識", roman: "CONSCIOUS", index: "04" },
  { kanji: "記録", roman: "RECORD", index: "05" },
];

export default function CodeSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Fractal spiral canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const cx = W * 0.5;
      const cy = H * 0.5;

      /* golden spiral arms */
      for (let arm = 0; arm < 5; arm++) {
        const armPhase = (arm / 5) * Math.PI * 2;
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= 200; i++) {
          const frac = i / 200;
          const angle = frac * Math.PI * 6 + armPhase + t * 0.006;
          const r = frac * Math.min(W, H) * 0.42;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          if (first) { ctx.moveTo(x, y); first = false; }
          else ctx.lineTo(x, y);
        }
        const alpha = 0.04 + Math.sin(t * 0.02 + arm) * 0.02;
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* concentric fractal rings */
      for (let ring = 1; ring <= 6; ring++) {
        const r = (ring / 6) * Math.min(W, H) * 0.44;
        const pulse = Math.sin(t * 0.015 + ring * 0.5) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${0.03 + pulse * 0.04})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* expanding pulse rings */
      for (let p = 0; p < 4; p++) {
        const phase = ((t * 0.004 + p * 0.25) % 1);
        const r = phase * Math.min(W, H) * 0.5;
        const alpha = (1 - phase) * 0.08;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* centre glow */
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 60);
      grd.addColorStop(0, "rgba(255,220,140,0.12)");
      grd.addColorStop(1, "rgba(255,200,100,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, 60, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      /* centre dot */
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,220,140,0.6)";
      ctx.fill();

      /* radial spokes */
      for (let s = 0; s < 12; s++) {
        const angle = (s / 12) * Math.PI * 2 + t * 0.003;
        const len = Math.min(W, H) * 0.44;
        const pulse = Math.sin(t * 0.02 + s * 0.5) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
        ctx.strokeStyle = `rgba(255,220,140,${0.015 + pulse * 0.02})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const header = entry.target.querySelector(".code-header");
            if (header) setTimeout(() => { (header as HTMLElement).style.opacity = "1"; }, 0);
            const items = entry.target.querySelectorAll(".code-item");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, 400 + i * 200);
            });
            const footer = entry.target.querySelector(".code-footer");
            if (footer) {
              setTimeout(() => { (footer as HTMLElement).style.opacity = "1"; }, 400 + codes.length * 200 + 500);
            }
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="codes"
      ref={sectionRef}
      className="relative w-full overflow-hidden flex items-center"
      style={{ background: "#020202" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-44 flex flex-col items-center text-center">
        {/* section label */}
        <div className="flex items-center gap-4 mb-14 md:mb-20">
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
          <p className="text-white/15 tracking-[0.5em] uppercase text-xs" style={{ fontFamily: "'Courier New', monospace" }}>
            code
          </p>
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
        </div>

        <p
          className="code-header text-white/45 font-light mb-14 md:mb-18"
          style={{
            fontSize: "clamp(16px, 2vw, 26px)",
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 300,
            letterSpacing: "0.1em",
            opacity: 0,
            transition: "opacity 2s ease",
          }}
        >
          5つのコードが存在する
        </p>

        <div className="flex flex-col gap-0 mb-16 md:mb-20 w-full max-w-sm">
          {codes.map((code, i) => (
            <div
              key={i}
              className="code-item group flex items-center justify-between py-4 md:py-5 border-b cursor-default"
              style={{
                borderColor: "rgba(255,220,140,0.06)",
                opacity: 0,
                transform: "translateY(16px)",
                transition: "opacity 1.4s ease, transform 1.4s ease",
              }}
            >
              <span
                className="text-white/15 text-xs"
                style={{ fontFamily: "'Courier New', monospace", letterSpacing: "0.2em" }}
              >
                {code.index}
              </span>
              <p
                className="text-white font-light"
                style={{
                  fontSize: "clamp(22px, 3vw, 44px)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.14em",
                  color: `rgba(255,255,255,${0.28 + i * 0.14})`,
                  textShadow: i === codes.length - 1 ? "0 0 30px rgba(255,220,140,0.2)" : "none",
                }}
              >
                {code.kanji}
              </p>
              <span
                className="text-white/15 text-[10px] tracking-[0.25em]"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                {code.roman}
              </span>
            </div>
          ))}
        </div>

        {/* ornament */}
        <div className="flex flex-col items-center gap-3 mb-10 md:mb-14">
          <div style={{ width: "1px", height: "40px", background: "linear-gradient(to bottom, rgba(255,220,140,0.3), transparent)" }} />
          <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "rgba(255,220,140,0.4)", boxShadow: "0 0 10px 3px rgba(255,200,100,0.2)" }} />
          <div style={{ width: "1px", height: "40px", background: "linear-gradient(to bottom, transparent, rgba(255,220,140,0.15))" }} />
        </div>

        <p
          className="code-footer text-white/28 font-light"
          style={{
            fontSize: "clamp(14px, 1.4vw, 17px)",
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 300,
            letterSpacing: "0.1em",
            opacity: 0,
            transition: "opacity 2.2s ease",
            lineHeight: 2.4,
          }}
        >
          それに触れる者だけが、<br />次に進む
        </p>
      </div>
    </section>
  );
}
